import React, { useEffect, useState } from "react";
import SVGComponent from "../_utils/SVGComponent";

import {
  GlobalConfiguration,
  TimelineBarConfiguration,
  TimelineConfiguration,
} from "../../utils/configurationProvider";

import CommonToast from "../_utils/CommonToast";
import Loader from "../Loader/Index";
import ICONS from "../../assets/Icons";

/**
 * CommonTimeLineCard Component
 *
 * Displays Approval (Stage 1) and Distribution (Stage 2) timelines
 * with status, duration, and progress visualization.
 *
 * @param {Object} props
 *
 * @param {Detail[]} props.logsData
 * Array of transaction details. Usually first element is used for rendering.
 *
 * @param {boolean} props.showApprovalDuration
 * Whether to show duration for approval stage
 *
 * @param {boolean} props.showDistributionDuration
 * Whether to show duration for distribution stage
 *
 * @param {OnClickEventHandler} props.handleApprovalClick
 * Triggered when user clicks on Approval section
 *
 * @param {OnClickEventHandler} props.handleDistributionClick
 * Triggered when user clicks on Distribution section
 *
 * @param {boolean} props.isDisabled
 * Disables interactions and shows restricted UI state
 *
 * @param {boolean} [props.leftArrowDisabled=false]
 * Disables left arrow navigation interaction
 *
 * @param {boolean} props.showDistributionRightIcon
 * Shows arrow icon on Distribution section
 *
 * @param {boolean} props.showApprovalLeftIcon
 * Shows arrow icon on Approval section
 *
 * @param {boolean} props.showApprovalTimeline
 * Controls selection state of approval timeline
 *
 * @param {boolean} props.showDistributionTimeline
 * Controls selection state of distribution timeline
 *
 * @param {boolean} props.popUpMode
 * Enables popup mode (restricts user interaction)
 *
 * @param {"single" | "double"} props.cardType
 * Layout type:
 *  - "single": only approval stage
 *  - "double": approval + distribution
 *
 *  * @param {string} props.leftCardTitle
 * shows left card title, default is "Stage 1 - Approval"
 *
 * @param {string} props.rightCardTitle
 * shows right card title, default is "Stage 2 - Distribution"
 *
 * @returns {JSX.Element}
 */

export const addLifeCycleStatus = ({
  lifecycle_status,
  distribution_lifecycle_status,
  services = [],
  previousStatus,
}) => {
  /* ---------- DEFAULT FALLBACK (ALWAYS SAFE) ---------- */
  let statusName = "Pending";
  let statusColor = "text-pending";

  let distributionStatusName = "N/A";
  let distributionStatusColor = "text-text-primary";

  let barColor = "progress-require-approval";
  let barValue = TimelineBarConfiguration.extrasmallwidth;

  /* ---------- MAIN LIFECYCLE STATUS ---------- */
  switch (lifecycle_status) {
    case "approval_pending":
      statusName = "Approval pending";
      statusColor = "text-pending";
      break;

    case "cancelled":
      statusName = "Validation rejected";
      statusColor = "text-failed";
      break;

    case "blocked":
      statusName = "Blocked";
      statusColor = "text-failed";
      break;

    case "created":
      statusName = services?.includes("ValidatorPrivateTransaction")
        ? "Require validation"
        : "Validation pending";
      statusColor = "text-pending";
      break;

    case "processing":
    case "uploaded":
      statusName = "Processing";
      statusColor = "text-queued";
      break;

    case "pending_for_otp":
    case "otp_requested":
    case "otp_submitted":
      statusName = services?.includes("OTPPrivateTransaction")
        ? "Require authentication"
        : "Authentication pending";
      statusColor = "text-pending";
      break;

    case "failed":
      statusName = "Process failed";
      statusColor = "text-failed";
      break;

    case "rejected":
      statusName = "Rejected";
      statusColor = "text-failed";
      break;

    case "abandoned":
      statusName = "Abandoned";
      statusColor = "text-abandoned";
      break;

    case "approved":
      statusName = "Approved";
      statusColor = "text-awaiting";
      break;
    case "deleted":
      statusName = "Discarded";
      statusColor = "text-abandoned";
      break;

    default:
      statusName = previousStatus
        ? previousStatus
        : services?.includes("APrivateTransaction")
          ? "Require approval"
          : "Pending";
      statusColor = "text-pending";
  }

  /* ---------- DISTRIBUTION STATUS ---------- */
  switch (distribution_lifecycle_status) {
    case "distribution_complete":
      distributionStatusName = "Distribution complete";
      distributionStatusColor = "text-settled";
      break;

    case "distribution_initiated":
      distributionStatusName = "Distribution initiated";
      distributionStatusColor = "text-queued";
      break;

    case "approved":
      distributionStatusName = "Distribution approved";
      distributionStatusColor = "text-positive-bg";
      break;

    case "created":
      distributionStatusName = "Distribution created";
      distributionStatusColor = "text-queued";
      break;

    case "awaiting_funds":
      distributionStatusName = "Awaiting funds";
      distributionStatusColor = "text-queued";
      break;

    case "rejected":
      distributionStatusName = "Distribution rejected";
      distributionStatusColor = "text-failed";
      break;

    case "failed":
      distributionStatusName = "Distribution failed";
      distributionStatusColor = "text-failed";
      break;

    case "abandoned":
      distributionStatusName = "Distribution abandoned";
      distributionStatusColor = "text-abandoned";
      break;

    default:
      distributionStatusName = "N/A";
      distributionStatusColor = "text-text-primary";
  }

  /* ---------- TIMELINE BAR LOGIC ---------- */
  if (
    ["rejected", "cancelled", "failed", "blocked"]?.includes(lifecycle_status)
  ) {
    barColor = "progress-rejected-approval";
    barValue = TimelineBarConfiguration.smallwidth;
  } else if (
    lifecycle_status === "abandoned" ||
    distribution_lifecycle_status === "abandoned"
  ) {
    barColor = "progress-abandoned-transaction";
    barValue =
      distribution_lifecycle_status === "abandoned"
        ? TimelineBarConfiguration.largewidth
        : TimelineBarConfiguration.smallwidth;
  } else {
    barColor = "progress-require-approval";
    barValue =
      distribution_lifecycle_status === "distribution_complete"
        ? TimelineBarConfiguration.largewidth
        : distribution_lifecycle_status
          ? TimelineBarConfiguration.mediumwidth
          : TimelineBarConfiguration.extrasmallwidth;
  }

  /* ---------- FINAL GUARANTEED RETURN ---------- */
  return {
    statusName,
    statusColor,
    distributionStatusName,
    distributionStatusColor,
    barColor,
    barValue,
  };
};

const CommonTimeLineCard = ({
  logsData,

  handleApprovalClick,
  handleDistributionClick,
  isDisabled,
  leftArrowDisabled = false,
  showDistributionRightIcon,
  showApprovalLeftIcon,
  showApprovalTimeline,
  showDistributionTimeline,
  popUpMode,
  cardType,
  leftCardTitle = "Stage 1 - Approval",
  rightCardTitle = "Stage 2 - Distribution",
}) => {
  const [approvalSelected, setApprovalSelected] = useState(null);

  const [distributionSelected, setDistributionSelected] = useState(null);

  const logs = logsData?.[0]?.distribution_logs || [];
  const logss = logsData?.[0]?.logs || [];

  const handleDistribution = () => {
    if (leftArrowDisabled) {
      return;
    } else if (isDisabled) {
      CommonToast({
        text: "Action restricted in popup mode.",
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
      });
      return;
    }
    setApprovalSelected(false);
    handleDistributionClick();
  };

  const handleApproval = () => {
    if (leftArrowDisabled) {
      return;
    } else if (isDisabled) {
      CommonToast({
        text: "Action restricted in popup mode.",
        icon: ICONS?.ERROR_WHITE_CIRCLE,
        variant: "var(--error)",
      });
      return;
    }
    handleApprovalClick();
    setApprovalSelected(true);
  };

  const getTotalDuration = (logs) => {
    if (!logs || logs.length < 2) return "0s";

    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    );
    const start = new Date(sortedLogs[0].timestamp);
    const end = new Date(sortedLogs[sortedLogs.length - 1].timestamp);
    const diffMs = end - start;

    return formatDuration(diffMs);
  };

  const getTotalDurationApproval = (logs) => {
    if (!logs || logs.length < 2) return "0s";

    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    );
    const start = new Date(sortedLogs[0].timestamp);
    const end = new Date(sortedLogs[sortedLogs.length - 1].timestamp);
    const diffMs = end - start;

    return formatDuration(diffMs);
  };
  const getDurationMs = (logs) => {
    if (!logs || logs.length < 2) return 0;

    const sortedLogs = [...logs].sort(
      (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
    );

    const start = new Date(sortedLogs[0].timestamp);
    const end = new Date(sortedLogs[sortedLogs.length - 1].timestamp);
    return end - start;
  };

  const formatDuration = (ms) => {
    if (ms <= 0) return "0s";

    if (ms < TimelineConfiguration.totalSeconds) return "1s";

    const seconds = Math.floor(ms / TimelineConfiguration.totalSeconds);
    const minutes = Math.floor(seconds / TimelineConfiguration.oneHour);
    const hours = Math.floor(minutes / TimelineConfiguration.oneHour);
    const days = Math.floor(hours / TimelineConfiguration.oneDay);

    if (days > 0) {
      const remHours = hours % TimelineConfiguration.oneDay;
      const remMinutes = minutes % TimelineConfiguration.oneHour;
      return `${days}d ${remHours}h ${remMinutes}m`;
    } else if (hours > 0) {
      const remMinutes = minutes % TimelineConfiguration.oneHour;
      return `${hours}h ${remMinutes}m`;
    } else if (minutes > 0) {
      const remSeconds = seconds % TimelineConfiguration.oneHour;
      return `${minutes}m ${remSeconds}s`;
    } else {
      const remMs = ms % TimelineConfiguration.totalSeconds;
      return remMs > 0 ? `${seconds}s ${remMs}ms` : `${seconds}s`;
    }
  };

  const totalDuration = getTotalDuration(logs);
  const duration1 = getDurationMs(logs);
  const duration2 = getDurationMs(logss);
  const totalDurationApproval = getTotalDurationApproval(logss);
  const totalDurationMs = duration1 + duration2;
  const totalDurationCombined = formatDuration(totalDurationMs);

  useEffect(() => {
    if (showApprovalTimeline) {
      setApprovalSelected(true);
    } else {
      setApprovalSelected(false);
    }

    if (showDistributionTimeline) {
      setDistributionSelected(true);
    } else {
      setDistributionSelected(false);
    }
  }, [showApprovalTimeline, showDistributionTimeline]);

  const index = GlobalConfiguration?.lengthCheckZero;

  const distributionStatus = logsData?.[index]?.distribution_lifecycle_status;

  const lifecycleStatus = logsData?.[index]?.lifecycle_status;

  const showDistributionDuration = [
    "rejected",
    "distribution_complete",
    "abandoned",
    "failed",
    "awaiting_funds",
  ].includes(distributionStatus);

  const showApprovalDuration = ![
    "pending_for_otp",
    "otp_requested",
    "otp_submitted",
    "processing",
    "uploaded",
    "pending",
    "failed",
    "created",
    "approval_pending",
  ].includes(lifecycleStatus);

  return (
    <div
      className={` bg-primary-nav relative w-full  pb-padding-sm ${cardType === "single" ? " z-z-index-base rounded-b-border-radius-md" : ""}`}
    >
      <div
        className={`flex relative Qsmd:flex-row flex-col`}
        style={{ cursor: isDisabled ? "not-allowed" : "default" }}
      >
        <div
          className={`absolute top-0 left-0 h-full ${cardType === "double" ? "Qsmd:w-width-md w-full" : "w-full"} transition-all duration-300 ease-in-out transform will-change-transform
          ${popUpMode ? "bg-primary-nav-act" : ""} 
          ${approvalSelected === true ? "translate-x-0" : approvalSelected === false ? "translate-x-full" : ""}`}
        />
        <div
          className={` pl-padding-md Qsmd:pr-padding-base pr-padding-md  w-full
          
            ${
              (popUpMode && cardType === "single") ||
              (approvalSelected && popUpMode)
                ? ""
                : leftArrowDisabled
                  ? "cursor-not-allowed"
                  : isDisabled
                    ? "cursor-not-allowed opacity-light-opacity"
                    : "cursor-pointer global-hover-white"
            }
            ${cardType === "double" ? "Qsmd:w-width-md" : "w-full"}  relative z-z-index-base`}
          onClick={handleApproval}
        >
          <div
            className={` ${cardType === "double" ? "Qsmd:border-r Qsmd:border-b-border-base border-b  border-r-border-base" : ""}  Qsmd:my-margin-sm mb-margin-base Qsmd:pb-padding-base pb-padding-sm border-primary-bg `}
          >
            <div className="mt-margin-sm flex justify-between items-center">
              <p className=" text-text-primary text-font-xs font-normal ">
                {leftCardTitle}
              </p>
              {showApprovalLeftIcon && (
                <>
                  <div
                    className={` Qsmd:mr-margin-md mr-margin-base  
                      ${leftArrowDisabled ? "cursor-not-allowed opacity-light-opacity" : ""}  
             `}
                  >
                    <SVGComponent
                      url={
                        "https://q2pay.in/new_assets/common/arrows/arrow_right_white.svg"
                      }
                      height={"var(--icon-sm)"}
                      width={"var(--icon-sm)"}
                      color={"var(--text-primary)"}
                    />
                  </div>
                </>
              )}
            </div>
            <div
              className={` text-font-sm ${logsData?.[0]?.statusColor}   font-semibold capitalize mt-margin-sm`}
            >
              {logsData?.[0]?.statusName || (
                <Loader
                  height="var(--icon-md)"
                  width="var(--icon-md)"
                />
              )}
            </div>
            <div className=" mt-margin-xs">
              <p className=" text-font-xxs text-text-secondary font-normal">
                Status
              </p>
            </div>
            {showApprovalDuration &&
              parseInt(totalDurationApproval) >
                GlobalConfiguration?.lengthCheckZero &&
              totalDurationApproval !==
                GlobalConfiguration?.lengthCheckZero && (
                <div className=" pt-padding-sm flex gap-gap-xs items-center">
                  <div className=" w-icon-xs">
                    <img
                      src="https://q2pay.in/new_assets/common/detail_page/outline_clock.svg"
                      alt=""
                    />
                  </div>

                  <p className=" text-font-xxs text-text-secondary font-normal">
                    {totalDurationApproval}
                  </p>
                </div>
              )}
          </div>
        </div>
        {cardType === "double" && (
          <div
            className={` pl-padding-md Qsmd:pr-padding-base pr-padding-md w-full  Qsmd:w-width-md  ${
              (popUpMode && cardType === "single") ||
              (distributionSelected && popUpMode)
                ? "z-z-index-modal"
                : logsData?.length >= GlobalConfiguration?.lengthCheckOne &&
                    logsData?.[GlobalConfiguration.lengthCheckZero]
                      ?.distribution_lifecycle_status &&
                    !isDisabled
                  ? "global-hover-white cursor-pointer relative z-z-index-base"
                  : " cursor-not-allowed"
            } ${isDisabled && "cursor-not-allowed"} `}
            onClick={
              logsData?.length >= GlobalConfiguration?.lengthCheckOne &&
              logsData?.[GlobalConfiguration.lengthCheckZero]
                ?.distribution_lifecycle_status
                ? handleDistribution
                : null
            }
          >
            <div
              className={`mt-margin-sm flex justify-between ${
                logsData?.length >= GlobalConfiguration?.lengthCheckOne &&
                logsData?.[GlobalConfiguration.lengthCheckZero]
                  ?.distribution_lifecycle_status &&
                !isDisabled
                  ? "opacity-base-opacity"
                  : "opacity-light-opacity"
              } items-center`}
            >
              <p className=" text-text-primary text-font-xs font-normal ">
                {rightCardTitle}
              </p>
              {showDistributionRightIcon && (
                <>
                  <div className=" Qsmd:mr-margin-md mr-margin-base">
                    <SVGComponent
                      url={
                        "https://q2pay.in/new_assets/common/arrows/arrow_right_white.svg"
                      }
                      height={"var(--icon-sm)"}
                      width={"var(--icon-sm)"}
                      color={"var(--text-primary)"}
                    />
                  </div>
                </>
              )}
            </div>

            <p
              className={`${logsData?.[GlobalConfiguration.lengthCheckZero]?.distributionStatusColor ? logsData?.[0]?.distributionStatusColor : "text-text-primary"} text-font-sm text-left font-sans font-semibold mt-margin-sm mb-margin-xs  ${logsData[0]?.distribution_lifecycle_status && !isDisabled ? "opacity-base-opacity" : "opacity-light-opacity"}`}
            >
              {`${logsData?.[GlobalConfiguration.lengthCheckZero]?.distributionStatusName || "NA"} `}
            </p>

            <div
              className={` mt-margin-xs Qsmd:mb-margin-base mb-margin-sm ${
                logsData?.[GlobalConfiguration.lengthCheckZero]
                  ?.distribution_lifecycle_status && !isDisabled
                  ? "opacity-base-opacity "
                  : "opacity-light-opacity "
              } `}
            >
              <p className=" text-font-xxs text-text-secondary font-normal">
                Status
              </p>
            </div>

            {showDistributionDuration && (
              <>
                {logsData?.[0]?.distribution_logs?.length >
                  GlobalConfiguration?.lengthCheckOne && (
                  <>
                    <div className="Qsmd:pt-padding-sm pb-padding-sm Qsmd:pb-padding-base flex gap-gap-xs items-center">
                      <div className=" w-icon-xs">
                        <img
                          src="https://q2pay.in/new_assets/common/detail_page/outline_clock.svg"
                          alt=""
                        />
                      </div>

                      <p className=" text-font-xxs text-text-secondary font-normal">
                        {totalDuration}
                      </p>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div>
        <div className={`px-padding-md  w-width-full  `}>
          <div className="bg-primary-bg w-width-full p-padding-xs rounded-border-radius-md relative flex items-center">
            <progress
              className={`w-width-full h-icon-xs ${logsData?.[0]?.barColor} top-top-base left-left-base`}
              value={
                logsData?.[0]?.barValue || GlobalConfiguration?.lengthCheckOne
              }
            />
          </div>
        </div>
      </div>

      {showDistributionDuration &&
        logsData?.[0]?.distribution_logs?.length >
          GlobalConfiguration?.lengthCheckOne && (
          <div className=" flex flex-col mt-margin-sm">
            <div className="flex items-center justify-center  text-text-secondary text-font-xxs font-normal">
              <div className="flex-grow border-t border-primary-act ml-margin-md"></div>
              <span className="mx-margin-sm">{totalDurationCombined}</span>
              <div className="flex-grow border-t border-primary-act mr-margin-md"></div>
            </div>
          </div>
        )}
    </div>
  );
};

export default CommonTimeLineCard;
